import React from 'react';
import { Card, CardContent } from './ui/card';
import { products } from '../data/mock';
import { Badge } from './ui/badge';
import { ChevronRight, Radio, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ProductsSection = () => {
  const { t } = useLanguage();

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Premium':
        return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
      case 'Compact':
        return { backgroundColor: '#e0f2fe', color: '#0369a1', borderColor: '#bae6fd' };
      case 'Standard':
        return { backgroundColor: '#E5E7EB', color: '#374151', borderColor: '#d1d5db' };
      default:
        return { backgroundColor: '#E5E7EB', color: '#374151' };
    }
  };

  return (
    <section id="produits" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6" style={{ color: '#1F4FD8' }}>
            {t.products.title}
            {t.products.titleHighlight}
          </h2>
          <p className="text-xl text-slate-600">
            {t.products.subtitle}
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {products.map((product, index) => (
            <Card
              key={product.id}
              className="group relative bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              {/* Product Image */}
              <div className="aspect-square flex items-center justify-center relative overflow-hidden p-6" style={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
                {product.image ? (
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                  />
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#1F4FD8' }}>
                      <Radio className="w-10 h-10 text-white" />
                    </div>
                    {/* Decorative rings */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-40 h-40 border-2 rounded-full opacity-50" style={{ borderColor: '#d1d5db' }} />
                      <div className="absolute w-56 h-56 border rounded-full opacity-30" style={{ borderColor: '#e5e7eb' }} />
                    </div>
                  </>
                )}
              </div>

              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" style={getCategoryColor(product.category)}>
                    {product.category}
                  </Badge>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {product.description}
                </p>
                <ul className="space-y-2">
                  {product.specs.map((spec, specIndex) => (
                    <li key={specIndex} className="flex items-start gap-2 text-sm text-slate-600">
                      <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#1F4FD8' }} />
                      {spec}
                    </li>
                  ))}
                </ul>
                {product.datasheet && (
                  <a
                    href={product.datasheet}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:-translate-y-0.5"
                    style={{ backgroundColor: '#1F4FD8', boxShadow: '0 4px 12px rgba(31, 79, 216, 0.25)' }}
                  >
                    <FileText className="w-4 h-4" />
                    {t.products.datasheet}
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-600 mb-2">
            {t.products.helpTitle}
          </p>
          <p className="text-slate-600 mb-4">
            {t.products.helpText}
          </p>
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 font-semibold transition-colors"
            style={{ color: '#1F4FD8' }}
          >
            {t.products.contactExperts}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
